import Text from '@embracesbs/component-text'
import { useContext, useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon'
import { Tenant } from '../../Interfaces/Types'
import { useApi, useAuth } from '../../Hooks'
import { AuthRoleContext } from '../Auth/AuthRoleContext'

// Reached from the confirmation mail sent by the pending-delete step, so it is often the
// first thing an operator sees after a context switch. It leads with what is about to be
// destroyed and how current that tenant is, and keeps the danger styling for the action
// itself rather than washing the whole page in warning colours.

const pageStyle = {
  maxWidth: '720px',
  margin: '0 auto'
}

const cardStyle = {
  background: 'var(--tma-panel-bg)',
  border: '1px solid var(--tma-list-card-border)',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
}

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  paddingBottom: '14px',
  borderBottom: '1px solid var(--tma-list-card-border)'
}

const metaGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, max-content) 1fr',
  columnGap: '20px',
  rowGap: '8px',
  margin: '18px 0 4px'
}

const dangerPanelStyle = {
  border: '1px solid var(--tma-danger-border)',
  background: 'var(--tma-danger-tint)',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '20px'
}

const neutralPanelStyle = {
  border: '1px solid var(--tma-list-card-border)',
  background: 'var(--tma-card-bg)',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '20px'
}

const errorStyle = {
  background: 'var(--tma-panel-bg)',
  border: '1px solid var(--tma-danger-border)',
  borderRadius: '8px',
  padding: '12px',
  marginTop: '14px'
}

const reasonInputStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  padding: '10px',
  marginTop: '10px',
  borderRadius: '6px',
  border: '1px solid var(--tma-card-border)',
  fontFamily: 'inherit',
  fontSize: '14px'
}

const buttonStyle = {
  marginTop: '16px'
}

// The teardown runs in a pipeline, so there is nothing to wait for once it is
// queued. Long enough to read the confirmation, short enough not to strand the
// operator on a dead-end page.
const REDIRECT_DELAY_MS = 8000

export default function DeleteTenant() {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const roles = useContext(AuthRoleContext)
  // tenant
  const [tenantError, setTenantError] = useState<Error | null>(null)
  const [tenantLoaded, setTenantLoaded] = useState(false)
  const [tenant, setTenant] = useState<Tenant>()

  // delete
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [canForce, setCanForce] = useState(false)
  const [forceReason, setForceReason] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleted, setIsDeleted] = useState(false)

  // other
  const [refresh, setRefresh] = useState(0)
  const backgrondRefresh = useRef(false)
  let params = useParams()
  let navigate = useNavigate()

  useEffect(() => {
    if (isLoggedIn === false) return
    const abortController = new AbortController()

    if (backgrondRefresh.current) {
      // do background refresh only this request
      backgrondRefresh.current = false
    } else {
      setTenantLoaded(false)
      setTenantError(null)
    }
    fetchTenantDetails(abortController)

    return () => {
      abortController.abort()
    }
  }, [params.tenantId, refresh, isLoggedIn])

  const fetchTenantDetails = (abortController: AbortController) => {
    fetchApi({
      endpoint: `/api/tenant/${params.tenantId}`,
      method: 'GET',
      signal: abortController.signal
    })
      .then(function (response) {
        setTenant(response)
        setTenantLoaded(true)
      })
      .catch(function (error) {
        console.log(error)
        if (!abortController.signal.aborted) {
          setTenantError(error)
        }
      })
  }

  useEffect(() => {
    // Stop polling once the teardown is queued: the tenant is on its way out,
    // and a refetch that 404s would replace the confirmation with an error.
    if (tenant && tenant.activeReleaseLock && !isDeleted) {
      const timeout = setTimeout(() => {
        backgrondRefresh.current = true
        setRefresh((r) => r + 1)
      }, 5000)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [tenant, isDeleted])

  const goToOverview = () => navigate(`/subscriptions/${tenant?.subscriptionId}`, { replace: true })

  // Hand the page back automatically, so an operator who walks away is not
  // parked on a tenant that no longer exists.
  useEffect(() => {
    if (!isDeleted) return
    const timeout = setTimeout(() => {
      navigate(`/subscriptions/${tenant?.subscriptionId}`, { replace: true })
    }, REDIRECT_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [isDeleted, tenant?.subscriptionId, navigate])

  // The API returns its refusal as a plain-text body, so show that rather than a
  // generic failure -- it names the tenant and how stale it is.
  const apiMessage = (error: any): string =>
    typeof error?.data === 'string' && error.data.length > 0 ? error.data : (error?.message ?? 'Something went wrong.')

  const handleDelete = (force = false) => {
    setIsDeleting(true)
    setDeleteError(null)

    const query = force ? `?force=true&reason=${encodeURIComponent(forceReason)}` : ''

    fetchApi({
      endpoint: `/api/tenant/${tenant?.id}${query}`,
      method: 'DELETE'
    })
      .then(() => {
        // Confirm before leaving: the teardown is queued, not finished, and the
        // operator should not be left guessing whether it took.
        setIsDeleting(false)
        setIsDeleted(true)
      })
      .catch((error: any) => {
        setIsDeleting(false)
        setDeleteError(apiMessage(error))
        // 409 is the freshness check refusing a stale tenant. Offer the override, which
        // needs a reason; anything else is a real error and should just be reported.
        if (error?.status === 409) setCanForce(true)
        console.log(error)
      })
  }

  const lastDeployLabel = tenant?.lastSuccessfulDeployUtc ? new Date(tenant.lastSuccessfulDeployUtc).toLocaleString() : 'never recorded'

  const metaRow = (label: string, value: string) => (
    <>
      <Text textStyle='description' textColor='grey' content={label} />
      <Text textStyle='body' content={value} />
    </>
  )

  // Takes precedence over the load/error states: once the teardown is queued the
  // tenant may already be gone, and a failed refetch must not hide the outcome.
  if (isDeleted) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={headerStyle}>
            <Icon iconName='CheckCircle1' size={20} color='var(--tma-success-text)' />
            <Text textStyle='h6' content={`Deletion started for ${tenant?.tenantDisplayname}`} />
          </div>
          <div style={{ marginTop: '14px' }}>
            <Text textStyle='body' content='The teardown pipeline is running in Azure DevOps.' />
          </div>
          <div style={{ marginTop: '6px' }}>
            <Text
              textStyle='description'
              textColor='grey'
              content='You do not have to wait for it — close this page or carry on working. The tenant disappears from the overview once the teardown finishes, and its pipeline shows the progress.'
            />
          </div>
          <Button2
            style={buttonStyle}
            text='Back to the overview'
            variant='contained'
            buttonRadius='8px'
            buttonSize='40px'
            onClick={goToOverview}
          />
          <div style={{ marginTop: '10px' }}>
            <Text textStyle='description' textColor='grey' content='Taking you back automatically…' />
          </div>
        </div>
      </div>
    )
  }

  if (tenantError) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Text textStyle='h6' content='Could not load this tenant' />
          <div style={{ marginTop: '8px' }}>
            <Text textStyle='body' textColor='grey' content={tenantError.message} />
          </div>
        </div>
      </div>
    )
  }

  if (!tenantLoaded) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Text textStyle='body' textColor='grey' content='Loading tenant…' />
        </div>
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <Icon iconName='Delete1' size={20} />
          <Text textStyle='h6' content={`Delete ${tenant?.tenantDisplayname}`} />
        </div>

        <div style={metaGridStyle}>
          {metaRow('Tenant name', tenant?.tenantName ?? '—')}
          {metaRow('Environment', tenant?.environment ?? '—')}
          {metaRow('Last successful deploy', lastDeployLabel)}
        </div>

        {/* roles are fetched from /api/getroles and start as null, so wait for them
            rather than flashing "not an administrator" at someone who is one. */}
        {tenant?.deployStatus === 'pendingDelete' && roles === null && (
          <div style={neutralPanelStyle}>
            <Text textStyle='body' textColor='grey' content='Checking your permissions…' />
          </div>
        )}

        {tenant?.deployStatus === 'pendingDelete' && roles !== null && !roles.isAdmin && (
          <div style={neutralPanelStyle}>
            <Text textStyle='body' content='Only administrators can delete a tenant.' />
            <div style={{ marginTop: '6px' }}>
              <Text
                textStyle='description'
                textColor='grey'
                content='Ask someone with the tenant administrator role to complete this deletion. The API enforces this too, so the delete would be rejected.'
              />
            </div>
          </div>
        )}

        {tenant?.deployStatus === 'pendingDelete' && roles?.isAdmin && (
          <div style={dangerPanelStyle}>
            <Text textStyle='h6' content='This cannot be undone' style={{ color: 'var(--tma-danger-text)' }} />
            <div style={{ margin: '8px 0 0' }}>
              <Text
                textStyle='description'
                textColor='grey'
                content={`All resources belonging to '${tenant?.tenantName}', including data, will be permanently destroyed.`}
              />
            </div>

            <Button2
              style={buttonStyle}
              text={isDeleting ? 'Deleting…' : 'Delete tenant'}
              isDanger
              isDisabled={isDeleting}
              leftElement={<Icon iconName='Delete1' size={16} />}
              variant='contained'
              buttonRadius='8px'
              buttonSize='40px'
              onClick={() => handleDelete(false)}
            ></Button2>

            {deleteError && (
              <div style={errorStyle}>
                <Text textStyle='description' style={{ color: 'var(--tma-danger-text)' }} content={deleteError} />
              </div>
            )}

            {canForce && (
              <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid var(--tma-danger-border)' }}>
                <Text textStyle='body' content='Delete it anyway' />
                <div style={{ margin: '6px 0 0' }}>
                  <Text
                    textStyle='description'
                    textColor='grey'
                    content='Updating the tenant first is safer, because the teardown then runs against the same module versions it was built with. If you continue, give a reason — it is written to the audit log.'
                  />
                </div>
                <input
                  type='text'
                  value={forceReason}
                  onChange={(e) => setForceReason(e.target.value)}
                  placeholder='Reason for forcing the delete'
                  style={reasonInputStyle}
                />
                <Button2
                  style={buttonStyle}
                  text='Force delete'
                  isDanger
                  isDisabled={isDeleting || forceReason.trim().length === 0}
                  variant='outlined'
                  buttonRadius='8px'
                  buttonSize='40px'
                  onClick={() => handleDelete(true)}
                ></Button2>
              </div>
            )}
          </div>
        )}

        {tenant?.deployStatus === 'deleting' && (
          <div style={neutralPanelStyle}>
            <Text textStyle='body' content='This tenant is already being deleted.' />
            <div style={{ marginTop: '6px' }}>
              <Text
                textStyle='description'
                textColor='grey'
                content='The teardown pipeline is running and this page refreshes itself, but you do not have to wait for it — the tenant disappears from the overview once it finishes.'
              />
            </div>
          </div>
        )}

        {tenant?.deployStatus !== 'pendingDelete' && tenant?.deployStatus !== 'deleting' && (
          <div style={neutralPanelStyle}>
            <Text textStyle='body' content={`'${tenant?.tenantName}' is not marked for deletion.`} />
            <div style={{ marginTop: '6px' }}>
              <Text
                textStyle='description'
                textColor='grey'
                content='Mark it for deletion from the tenant detail page first; that sends a fresh confirmation link.'
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
